import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { Badge } from '../components/Badge';
import { HelpTip } from '../components/HelpTip';
import { VerInstructivo } from '../components/VerInstructivo';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import { useServerPage } from '../hooks/useServerPage';
import { api, extractErrorMessage } from '../api/client';
import { ChipsVariante } from '../components/ChipsVariante';
import type { VariantesDeSku } from '../lib/variantes';
import { CodigoArticulo } from '../components/CodigoArticulo';
import { etiquetaArticulo, textoArticulo, useFilasConIdentidad, useIdentidades, type IdentidadArticulo } from '../lib/identidadArticulos';

type Origen = 'cancelada_post_despacho' | 'no_entregado' | 'reclamo';
type EstadoDevolucion = 'esperada' | 'recibida' | 'no_vuelve';
type ACargoDe = 'mensajeria' | 'cliente' | 'empresa';
type Accion = 'baja' | 'devolver_proveedor' | 'liberar';
type Pestana = 'esperadas' | 'recibidas' | 'cuarentena';

/** Una devolución (GET devoluciones): lo que se espera que vuelva de una venta cuyo stock ya salió, por SKU hoja. */
interface Devolucion {
  id: number;
  ordenVentaId: number;
  origen: Origen;
  estado: EstadoDevolucion;
  mlOrderId: string | null;
  mlClaimId: string | null;
  mlShipmentId: string | null;
  cliente: string | null;
  detectadaEn: string;
  diasEsperando: number | null;
  cerradaEn: string | null;
  cerradaPor: string | null;
  aCargoDe: ACargoDe | null;
  notas: string | null;
  items: ItemDevolucion[];
}

/** Color de una línea: la devolución trae una línea por SKU y color vendido (la línea sin color es el resto del SKU). */
interface ColorLinea { id: number; nombre: string; colorHex: string | null }
interface ItemDevolucion {
  skuInterno: string; descripcion: string | null; cantidadEsperada: number; cantidadBuenEstado: number; cantidadFalla: number; cantidadNoVuelve: number;
  varianteId?: number | null; variante?: ColorLinea | null; varianteRecibidaId?: number | null; varianteRecibida?: ColorLinea | null;
}
const claveLinea = (i: ItemDevolucion) => `${i.skuInterno}|${i.varianteId ?? ''}`;
type Identidad = (sku?: string | null) => IdentidadArticulo | undefined;
/** Código del artículo (con el proveedor si el código se repite) y, si la línea es de un color, el color vendido. */
const etiquetaLinea = (i: ItemDevolucion, identidad: Identidad) => {
  const articulo = etiquetaArticulo(i.skuInterno, identidad(i.skuInterno));
  return i.variante ? `${articulo} · ${i.variante.nombre}` : articulo;
};
const textoEsperadas = (d: Devolucion, identidad: Identidad) =>
  d.items.map((i) => `${etiquetaLinea(i, identidad)} × ${fmtCantidad(i.cantidadEsperada)}`);
const textoRecibidas = (d: Devolucion, identidad: Identidad) => d.items.map((i) => `${etiquetaLinea(i, identidad)}: ${fmtCantidad(i.cantidadBuenEstado)} en buen estado${i.varianteRecibida && i.varianteRecibidaId !== i.varianteId ? ` (subió ${i.varianteRecibida.nombre})` : ''} · ${fmtCantidad(i.cantidadFalla)} con falla · ${fmtCantidad(i.cantidadNoVuelve)} no volvió`);

/** Un SKU en cuarentena (GET cuarentena): devoluciones con falla, en el depósito pero no se venden ni se publican. */
interface SkuCuarentena {
  skuInterno: string;
  descripcion: string | null;
  cuarentena: number;
  fisico: number;
  reservado: number;
  disponible: number;
  /** Colores activos del SKU: liberar pide a cuál vuelven las unidades (vacío: el SKU no vende por colores). */
  variantes?: ColorLinea[];
  historial: Array<{ id: number; tipo: string; cantidad: number; devolucionId: number | null; fecha: string; notas: string | null; usuario: string | null }>;
}

interface Ubicacion { id: number; codigo: string }

const ORIGEN: Record<Origen, { etiqueta: string; explicacion: string }> = {
  cancelada_post_despacho: { etiqueta: 'Venta cancelada después del despacho', explicacion: 'La venta se canceló cuando la mercadería ya había salido del depósito.' },
  no_entregado: { etiqueta: 'Envío no entregado', explicacion: 'Mercado Libre informó el envío como no entregado (o volviendo al vendedor).' },
  reclamo: { etiqueta: 'Reclamo con devolución', explicacion: 'El comprador abrió un reclamo en Mercado Libre y la mercadería vuelve.' },
};
const ESTADO: Record<EstadoDevolucion, { etiqueta: string; tono: 'amber' | 'green' | 'slate' }> = {
  esperada: { etiqueta: 'Esperada', tono: 'amber' },
  recibida: { etiqueta: 'Recibida', tono: 'green' },
  no_vuelve: { etiqueta: 'No vuelve', tono: 'slate' },
};
const A_CARGO_DE: Record<ACargoDe, string> = { mensajeria: 'Mensajería', cliente: 'Cliente', empresa: 'Empresa' };
const ACCION: Record<Accion, { boton: string; titulo: string; explicacion: string }> = {
  baja: {
    boton: 'Dar de baja', titulo: 'Dar de baja de cuarentena',
    explicacion: 'La falla no tiene arreglo: las unidades salen del depósito (baja por rotura) y de la cuarentena.',
  },
  devolver_proveedor: {
    boton: 'Devolver al proveedor', titulo: 'Devolver al proveedor',
    explicacion: 'Las unidades salen del depósito como devolución al proveedor y de la cuarentena.',
  },
  liberar: {
    boton: 'Liberar (reparado)', titulo: 'Liberar de cuarentena (reparado)',
    explicacion: 'Las unidades se repararon: siguen en el depósito y vuelven a estar a la venta (se publican).',
  },
};
const TIPO_HISTORIAL: Record<string, string> = { ingreso: 'Ingreso', baja: 'Baja', devolucion_proveedor: 'Devuelto al proveedor', liberacion: 'Liberado', ajuste_conteo: 'Ajuste por conteo' };

const PERMISO_DENEGADO = 'No tenés permiso para realizar esta acción';
const ENDPOINT = '/api/v3/devoluciones';

const fmtFecha = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('es-AR') : '—');
const fmtCantidad = (n: number) => Number(n).toLocaleString('es-AR', { maximumFractionDigits: 4 });
const fmtDias = (d: number | null) => (d == null ? '—' : d === 0 ? 'hoy' : d === 1 ? '1 día' : `${d} días`);
const conSigno = (n: number) => (n > 0 ? `+${fmtCantidad(n)}` : `−${fmtCantidad(-n)}`);
const numero = (v: string) => {
  const n = Number(v.replace(',', '.'));
  return v.trim() === '' ? 0 : Number.isFinite(n) ? n : NaN;
};
const redondear = (n: number) => Math.round(n * 1e4) / 1e4;

const claseInput = 'w-full border border-slate-300 rounded-md px-3 py-2 text-sm';
const claseBotonLink = 'text-brand-600 hover:text-brand-800 text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';

function OrigenDe({ d }: { d: Devolucion }) {
  const o = ORIGEN[d.origen];
  return <span title={o?.explicacion}>{o?.etiqueta ?? d.origen}</span>;
}

export function DevolucionesPage() {
  const toast = useToast();
  const { can } = useAuth();
  const puedeEditar = can('wms', 'editar');
  const [searchParams, setSearchParams] = useSearchParams();
  const ov = searchParams.get('ov') ?? '';

  const [pestana, setPestana] = useState<Pestana>('esperadas');
  const [origen, setOrigen] = useState('');
  const [estadoCerrada, setEstadoCerrada] = useState<'recibida' | 'no_vuelve'>('recibida');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [recibiendo, setRecibiendo] = useState<Devolucion | null>(null);
  const [cerrando, setCerrando] = useState<Devolucion | null>(null);
  const [accion, setAccion] = useState<{ sku: SkuCuarentena; accion: Accion } | null>(null);

  // Cantidad de esperadas para la pestaña (sólo el total).
  const conteo = useServerPage<Devolucion>(ENDPOINT, 1, 1, '', 'q', { estado: 'esperada' }, { serverSideFilters: true });
  const filtros: Record<string, string> = ov
    ? { orden_venta_id: ov }
    : { estado: pestana === 'recibidas' ? estadoCerrada : 'esperada', origen };
  const lista = useServerPage<Devolucion>(ENDPOINT, page, pageSize, '', 'q', filtros, { serverSideFilters: true });
  const cuarentena = useApi<SkuCuarentena[]>(pestana === 'cuarentena' && !ov ? '/api/v3/cuarentena' : null, [pestana, ov]);

  // Codes as users see them; the rows carry the same texts for the CSV/XLSX export.
  const identidad = useIdentidades((lista.data ?? []).flatMap((d) => d.items.map((i) => i.skuInterno)));
  const filasLista = (lista.data ?? []).map((d) => ({
    ...d,
    skuYCantidades: textoEsperadas(d, identidad).join(', '),
    queVolvio: textoRecibidas(d, identidad).join(', '),
    proveedorArticulo: [...new Set(d.items.flatMap((i) => identidad(i.skuInterno)?.proveedor?.nombre ?? []))].join(', '),
  }));
  const filasCuarentena = useFilasConIdentidad(cuarentena.data ?? [], (s) => s.skuInterno);

  const recargar = () => { lista.refetch(); conteo.refetch(); cuarentena.refetch(); };
  const cambiarPestana = (p: Pestana) => { setPestana(p); setPage(1); };

  const acciones = (d: Devolucion) => {
    if (d.estado !== 'esperada') return null;
    const motivo = puedeEditar ? undefined : PERMISO_DENEGADO;
    return (
      <div className="flex flex-col items-start gap-1">
        <button onClick={() => setRecibiendo(d)} disabled={!!motivo} title={motivo} className={claseBotonLink}>Recibir devolución</button>
        <button onClick={() => setCerrando(d)} disabled={!!motivo} title={motivo} className={claseBotonLink}>No vuelve</button>
      </div>
    );
  };

  const columnasEsperadas = [
    { key: 'detectadaEn', header: 'Detectada', render: (d: Devolucion) => fmtFecha(d.detectadaEn) },
    { key: 'ordenVentaId', header: 'OV #', render: (d: Devolucion) => `OV #${d.ordenVentaId}` },
    { key: 'mlOrderId', header: 'Orden ML', render: (d: Devolucion) => d.mlOrderId ?? '—' },
    { key: 'origen', header: 'Origen', render: (d: Devolucion) => <OrigenDe d={d} /> },
    {
      key: 'diasEsperando', header: 'Esperando', help: 'Días desde que se detectó la devolución.',
      render: (d: Devolucion) => (ov ? <Badge tone={ESTADO[d.estado]?.tono ?? 'slate'}>{ESTADO[d.estado]?.etiqueta ?? d.estado}</Badge> : fmtDias(d.diasEsperando)),
    },
    {
      key: 'skuYCantidades', header: 'Código y cantidades',
      render: (d: Devolucion) => (
        <div className="space-y-0.5">
          {textoEsperadas(d, identidad).map((t, n) => <div key={claveLinea(d.items[n])} title={d.items[n].descripcion ?? undefined}>{t}</div>)}
        </div>
      ),
    },
    { key: 'proveedorArticulo', header: 'Proveedor' },
    { key: 'notas', header: 'Notas', render: (d: Devolucion) => <span className="text-xs text-slate-600">{d.notas ?? '—'}</span> },
    { key: 'acciones', header: '', render: acciones },
  ];

  const columnasRecibidas = [
    { key: 'cerradaEn', header: 'Cerrada', render: (d: Devolucion) => fmtFecha(d.cerradaEn) },
    { key: 'ordenVentaId', header: 'OV #', render: (d: Devolucion) => `OV #${d.ordenVentaId}` },
    { key: 'origen', header: 'Origen', render: (d: Devolucion) => <OrigenDe d={d} /> },
    { key: 'estado', header: 'Estado', render: (d: Devolucion) => <Badge tone={ESTADO[d.estado]?.tono ?? 'slate'}>{ESTADO[d.estado]?.etiqueta ?? d.estado}</Badge> },
    {
      key: 'queVolvio', header: 'Qué volvió',
      render: (d: Devolucion) => (
        <div className="space-y-0.5">
          {textoRecibidas(d, identidad).map((t, n) => <div key={claveLinea(d.items[n])} title={d.items[n].descripcion ?? undefined}>{t}</div>)}
        </div>
      ),
    },
    { key: 'proveedorArticulo', header: 'Proveedor' },
    { key: 'cerradaPor', header: 'Recibió', render: (d: Devolucion) => d.cerradaPor ?? '—' },
    { key: 'aCargoDe', header: 'Lo que no volvió, a cargo de', render: (d: Devolucion) => (d.aCargoDe ? A_CARGO_DE[d.aCargoDe] ?? d.aCargoDe : '—') },
    { key: 'notas', header: 'Notas', render: (d: Devolucion) => <span className="text-xs text-slate-600">{d.notas ?? '—'}</span> },
  ];

  const columnasCuarentena = [
    { key: 'codigoArticulo', header: 'Código', render: (s: SkuCuarentena) => <CodigoArticulo sku={s.skuInterno} /> },
    { key: 'proveedorArticulo', header: 'Proveedor' },
    { key: 'descripcion', header: 'Descripción', render: (s: SkuCuarentena) => s.descripcion ?? '—' },
    {
      key: 'cuarentena', header: 'En cuarentena', help: 'Volvieron con falla: están en el depósito pero no se venden ni se publican.',
      render: (s: SkuCuarentena) => <strong className="text-amber-700">{fmtCantidad(s.cuarentena)}</strong>,
    },
    {
      key: 'stock', header: 'Stock',
      render: (s: SkuCuarentena) => (
        <span className="text-xs text-slate-600 whitespace-nowrap">
          {`Físico ${fmtCantidad(s.fisico)} · Reservado ${fmtCantidad(s.reservado)} · Disponible ${fmtCantidad(s.disponible)}`}
        </span>
      ),
    },
    {
      key: 'historial', header: 'Historial',
      render: (s: SkuCuarentena) => (
        <ul className="text-xs text-slate-600 space-y-0.5">
          {s.historial.map((h) => (
            <li key={h.id} title={h.notas ?? undefined}>
              {`${TIPO_HISTORIAL[h.tipo] ?? h.tipo}${h.devolucionId != null ? ` (devolución #${h.devolucionId})` : ''}: ${conSigno(h.cantidad)} · ${fmtFecha(h.fecha)}${h.usuario ? ` · ${h.usuario}` : ''}`}
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'acciones', header: '',
      render: (s: SkuCuarentena) => {
        const motivo = puedeEditar ? undefined : PERMISO_DENEGADO;
        return (
          <div className="flex flex-col items-start gap-1">
            {(Object.keys(ACCION) as Accion[]).map((a) => (
              <button key={a} onClick={() => setAccion({ sku: s, accion: a })} disabled={!!motivo} title={motivo} className={claseBotonLink}>
                {ACCION[a].boton}
              </button>
            ))}
          </div>
        );
      },
    },
  ];

  const pestanas: Array<[Pestana, string]> = [['esperadas', 'Esperadas'], ['recibidas', 'Recibidas'], ['cuarentena', 'Cuarentena']];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 inline">Devoluciones</h1>
        <HelpTip
          tips={[
            'Lo que vuelve de una venta cuyo stock ya salió del depósito: ventas canceladas después del despacho, envíos de Mercado Libre no entregados y reclamos con devolución. El sistema las detecta solo (sólo lee Mercado Libre; nunca escribe).',
            'Al recibir, por cada SKU: en buen estado vuelve al stock a la venta; con falla entra al depósito en cuarentena (no se vende ni se publica); lo que falta no vuelve.',
            'En Cuarentena, cada SKU se da de baja, se devuelve al proveedor o se libera cuando se repara.',
          ]}
        />
        <VerInstructivo seccion="devoluciones" />
        <p className="text-slate-500 text-sm mt-1">Devoluciones esperadas de ventas, recepción y cuarentena</p>
      </div>

      {ov ? (
        <div className="flex flex-wrap items-center gap-3 bg-brand-50 border border-brand-200 rounded-lg px-4 py-2">
          <span className="text-sm font-medium text-slate-700">{`Devoluciones de la OV #${ov}`}</span>
          <button type="button" className={claseBotonLink} onClick={() => { searchParams.delete('ov'); setSearchParams(searchParams); setPage(1); }}>
            Ver todas
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 border-b border-slate-200" role="tablist">
          {pestanas.map(([k, label]) => (
            <button
              key={k} type="button" role="tab" aria-selected={pestana === k} onClick={() => cambiarPestana(k)}
              className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${pestana === k ? 'border-brand-500 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              {label}
              {k === 'esperadas' && conteo.total > 0 && <Badge tone="amber" className="ml-2">{conteo.total}</Badge>}
            </button>
          ))}
        </div>
      )}

      {!ov && pestana !== 'cuarentena' && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label htmlFor="dv-origen" className="block text-xs font-medium text-slate-600 mb-1">Origen</label>
            <select id="dv-origen" className={claseInput} value={origen} onChange={(e) => { setOrigen(e.target.value); setPage(1); }}>
              <option value="">Todos</option>
              {(Object.keys(ORIGEN) as Origen[]).map((o) => <option key={o} value={o}>{ORIGEN[o].etiqueta}</option>)}
            </select>
          </div>
          {pestana === 'recibidas' && (
            <div>
              <label htmlFor="dv-estado" className="block text-xs font-medium text-slate-600 mb-1">Estado</label>
              <select id="dv-estado" className={claseInput} value={estadoCerrada} onChange={(e) => { setEstadoCerrada(e.target.value as 'recibida' | 'no_vuelve'); setPage(1); }}>
                <option value="recibida">Recibidas</option>
                <option value="no_vuelve">No vuelven</option>
              </select>
            </div>
          )}
        </div>
      )}

      {pestana === 'cuarentena' && !ov ? (
        <>
          {cuarentena.error && <p className="text-red-600 text-sm">{cuarentena.error}</p>}
          <DataTable
            columns={columnasCuarentena}
            data={filasCuarentena}
            loading={cuarentena.loading}
            emptyMessage="No hay unidades en cuarentena"
            title="Cuarentena"
          />
        </>
      ) : (
        <>
          {lista.error && <p className="text-red-600 text-sm">{lista.error}</p>}
          <DataTable
            columns={pestana === 'recibidas' && !ov ? columnasRecibidas : columnasEsperadas}
            data={filasLista}
            loading={lista.loading}
            emptyMessage={pestana === 'recibidas' && !ov ? 'Sin devoluciones cerradas para los filtros' : 'No hay devoluciones esperadas'}
            title="Devoluciones"
          />
          <Pagination
            page={page}
            pageSize={pageSize}
            total={lista.total}
            onChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </>
      )}

      {recibiendo && (
        <DialogoRecibir
          devolucion={recibiendo}
          onClose={() => setRecibiendo(null)}
          onListo={(mensaje) => { setRecibiendo(null); toast.addToast(mensaje, 'success'); recargar(); }}
          onError={(m) => toast.addToast(m, 'error')}
        />
      )}
      {cerrando && (
        <DialogoNoVuelve
          devolucion={cerrando}
          onClose={() => setCerrando(null)}
          onListo={() => { setCerrando(null); toast.addToast(`Devolución #${cerrando.id} cerrada: no vuelve`, 'success'); recargar(); }}
          onError={(m) => toast.addToast(m, 'error')}
        />
      )}
      {accion && (
        <DialogoAccionCuarentena
          sku={accion.sku}
          accion={accion.accion}
          onClose={() => setAccion(null)}
          onListo={() => { setAccion(null); toast.addToast(`${ACCION[accion.accion].boton}: ${textoArticulo(accion.sku.skuInterno, identidad(accion.sku.skuInterno))} listo`, 'success'); recargar(); }}
          onError={(m) => toast.addToast(m, 'error')}
        />
      )}
    </div>
  );
}

interface PropsDialogo {
  onClose: () => void;
  onError: (mensaje: string) => void;
}

function DialogoRecibir({ devolucion, onClose, onListo, onError }: PropsDialogo & { devolucion: Devolucion; onListo: (mensaje: string) => void }) {
  const { data: ubicacionesResp } = useApi<Ubicacion[]>('/api/v3/ubicaciones?limit=500&offset=0');
  const ubicaciones = Array.isArray(ubicacionesResp) ? ubicacionesResp : [];
  const [ubicacion, setUbicacion] = useState<string | null>(null);
  // Con una sola ubicación, preseleccionada; si no, la general (automática).
  const ubicacionElegida = ubicacion ?? (ubicaciones.length === 1 ? String(ubicaciones[0].id) : '');
  const [cantidades, setCantidades] = useState<Record<string, { buen: string; falla: string }>>(
    () => Object.fromEntries(devolucion.items.map((i) => [claveLinea(i), { buen: String(i.cantidadEsperada), falla: '0' }])),
  );
  // Color al que vuelve lo que llega en buen estado: el vendido, editable (mandaron otro color). Sin variantes en el SKU no se pregunta.
  const [colores, setColores] = useState<Record<string, number | null>>(
    () => Object.fromEntries(devolucion.items.map((i) => [claveLinea(i), i.varianteId ?? null])),
  );
  const [variantes, setVariantes] = useState<Record<string, VariantesDeSku>>({});
  useEffect(() => {
    let vigente = true;
    for (const sku of new Set(devolucion.items.map((i) => i.skuInterno))) {
      api.get<VariantesDeSku>(`/api/v3/catalogo/${encodeURIComponent(sku)}/variantes`)
        .then((v) => { if (vigente && v?.variantes) setVariantes((prev) => ({ ...prev, [sku]: v })); })
        .catch(() => { /* sin colores: el backend usa el vendido */ });
    }
    return () => { vigente = false; };
  }, [devolucion.items]);
  const identidad = useIdentidades(devolucion.items.map((i) => i.skuInterno));
  const coloresDe = (i: ItemDevolucion) => (variantes[i.skuInterno]?.variantes ?? []).filter((v) => v.activo || v.id === colores[claveLinea(i)]);
  const [aCargoDe, setACargoDe] = useState<ACargoDe>('mensajeria');
  const [notas, setNotas] = useState('');
  const [enviando, setEnviando] = useState(false);

  const lineas = useMemo(() => devolucion.items.map((i) => {
    const c = cantidades[claveLinea(i)] ?? { buen: '0', falla: '0' };
    const buen = numero(c.buen);
    const falla = numero(c.falla);
    const invalida = !Number.isFinite(buen) || !Number.isFinite(falla) || buen < 0 || falla < 0;
    const excede = !invalida && buen + falla > i.cantidadEsperada + 1e-4;
    return { ...i, buen, falla, invalida, excede, noVuelve: invalida ? 0 : Math.max(0, redondear(i.cantidadEsperada - buen - falla)) };
  }), [cantidades, devolucion.items]);
  const errores = lineas.flatMap((l) => (l.invalida
    ? [`${etiquetaLinea(l, identidad)}: cantidades inválidas`]
    : l.excede ? [`${etiquetaLinea(l, identidad)}: se esperaban ${fmtCantidad(l.cantidadEsperada)} y se quieren recibir ${fmtCantidad(redondear(l.buen + l.falla))}`] : []));
  const faltante = lineas.some((l) => l.noVuelve > 0);

  const cambiar = (clave: string, campo: 'buen' | 'falla', valor: string) =>
    setCantidades((prev) => ({ ...prev, [clave]: { ...(prev[clave] ?? { buen: '0', falla: '0' }), [campo]: valor } }));

  const confirmar = async () => {
    setEnviando(true);
    try {
      await api.post(`/api/v3/devoluciones/${devolucion.id}/recibir`, {
        items: lineas.map((l) => ({
          skuInterno: l.skuInterno, varianteId: l.varianteId ?? null,
          ...(variantes[l.skuInterno]?.variantes.length ? { varianteRecibidaId: colores[claveLinea(l)] ?? null } : {}),
          cantidadBuenEstado: l.buen, cantidadFalla: l.falla,
        })),
        ...(ubicacionElegida ? { ubicacionId: Number(ubicacionElegida) } : {}),
        ...(faltante ? { aCargoDe } : {}),
        ...(notas.trim() ? { notas: notas.trim() } : {}),
      });
      const volvio = lineas.some((l) => l.buen + l.falla > 0);
      onListo(volvio ? `Devolución #${devolucion.id} recibida` : `Devolución #${devolucion.id} cerrada: no vuelve`);
    } catch (e) {
      onError(extractErrorMessage(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Recibir devolución #${devolucion.id} · OV #${devolucion.ordenVentaId}`} size="lg">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Por cada SKU, cuántas unidades volvieron en buen estado (vuelven a la venta) y cuántas con falla (entran al depósito en
          cuarentena: no se venden ni se publican). Lo que falta no vuelve.
        </p>
        <div>
          <label htmlFor="dv-ubicacion" className="block text-xs font-medium text-slate-600 mb-1">Ubicación</label>
          <select id="dv-ubicacion" className={claseInput} value={ubicacionElegida} onChange={(e) => setUbicacion(e.target.value)}>
            <option value="">Depósito general (automático)</option>
            {ubicaciones.map((u) => <option key={u.id} value={String(u.id)}>{u.codigo}</option>)}
          </select>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500">
              <th className="py-1">SKU</th><th className="py-1">Esperadas</th><th className="py-1">En buen estado</th><th className="py-1">Con falla</th><th className="py-1">No vuelven</th>
            </tr>
          </thead>
          <tbody>
            {lineas.map((l) => (
              <tr key={claveLinea(l)} className="border-t border-slate-100">
                <td className="py-1 pr-2">
                  <div className="font-medium">{etiquetaLinea(l, identidad)}</div><div className="text-xs text-slate-500">{l.descripcion ?? ''}</div>
                  {coloresDe(l).length > 0 && (
                    <div className="mt-1">
                      <ChipsVariante etiqueta={`Color al que vuelve ${etiquetaLinea(l, identidad)}`} variantes={coloresDe(l)} valor={colores[claveLinea(l)] ?? null}
                        onChange={(id) => setColores((prev) => ({ ...prev, [claveLinea(l)]: id }))} />
                    </div>
                  )}
                </td>
                <td className="py-1 pr-2">{fmtCantidad(l.cantidadEsperada)}</td>
                <td className="py-1 pr-2">
                  <input type="number" min={0} step="any" aria-label={`${etiquetaLinea(l, identidad)} en buen estado`} className={claseInput}
                    value={cantidades[claveLinea(l)]?.buen ?? ''} onChange={(e) => cambiar(claveLinea(l), 'buen', e.target.value)} />
                </td>
                <td className="py-1 pr-2">
                  <input type="number" min={0} step="any" aria-label={`${etiquetaLinea(l, identidad)} con falla`} className={claseInput}
                    value={cantidades[claveLinea(l)]?.falla ?? ''} onChange={(e) => cambiar(claveLinea(l), 'falla', e.target.value)} />
                </td>
                <td className="py-1" data-testid={`no-vuelve-${l.skuInterno}${l.varianteId != null ? `-${l.varianteId}` : ''}`}>{fmtCantidad(l.noVuelve)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {errores.length > 0 && <ul className="text-sm text-red-600">{errores.map((m) => <li key={m}>{m}</li>)}</ul>}
        {faltante && (
          <div>
            <label htmlFor="dv-a-cargo" className="block text-xs font-medium text-slate-600 mb-1">Lo que no vuelve queda a cargo de</label>
            <select id="dv-a-cargo" className={claseInput} value={aCargoDe} onChange={(e) => setACargoDe(e.target.value as ACargoDe)}>
              {(Object.keys(A_CARGO_DE) as ACargoDe[]).map((a) => <option key={a} value={a}>{A_CARGO_DE[a]}</option>)}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="dv-notas" className="block text-xs font-medium text-slate-600 mb-1">Notas</label>
          <textarea id="dv-notas" className={claseInput} rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancelar</button>
          <button onClick={confirmar} disabled={enviando || errores.length > 0}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed">
            {enviando ? 'Guardando…' : 'Confirmar recepción'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DialogoNoVuelve({ devolucion, onClose, onListo, onError }: PropsDialogo & { devolucion: Devolucion; onListo: () => void }) {
  const [aCargoDe, setACargoDe] = useState<ACargoDe>('mensajeria');
  const [notas, setNotas] = useState('');
  const [enviando, setEnviando] = useState(false);
  const confirmar = async () => {
    setEnviando(true);
    try {
      await api.post(`/api/v3/devoluciones/${devolucion.id}/no-vuelve`, { aCargoDe, ...(notas.trim() ? { notas: notas.trim() } : {}) });
      onListo();
    } catch (e) {
      onError(extractErrorMessage(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={`No vuelve · devolución #${devolucion.id} (OV #${devolucion.ordenVentaId})`} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">La devolución se cierra sin que entre nada al depósito (p. ej. el envío se perdió o el cliente se quedó la mercadería).</p>
        <div>
          <label htmlFor="nv-a-cargo" className="block text-xs font-medium text-slate-600 mb-1">A cargo de</label>
          <select id="nv-a-cargo" className={claseInput} value={aCargoDe} onChange={(e) => setACargoDe(e.target.value as ACargoDe)}>
            {(Object.keys(A_CARGO_DE) as ACargoDe[]).map((a) => <option key={a} value={a}>{A_CARGO_DE[a]}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="nv-notas" className="block text-xs font-medium text-slate-600 mb-1">Notas</label>
          <textarea id="nv-notas" className={claseInput} rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancelar</button>
          <button onClick={confirmar} disabled={enviando}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-500 disabled:opacity-50">
            Confirmar
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DialogoAccionCuarentena({ sku, accion, onClose, onListo, onError }: PropsDialogo & { sku: SkuCuarentena; accion: Accion; onListo: () => void }) {
  const [cantidad, setCantidad] = useState(String(sku.cuarentena));
  const identidad = useIdentidades([sku.skuInterno]);
  const [notas, setNotas] = useState('');
  const [varianteId, setVarianteId] = useState<number | null>(null);
  const [enviando, setEnviando] = useState(false);
  const q = numero(cantidad);
  // Liberar vuelve a la venta: si el SKU vende por colores hay que elegir a cuál (baja y devolver no son de ningún color).
  const pideColor = accion === 'liberar' && (sku.variantes?.length ?? 0) > 0;
  const valida = Number.isFinite(q) && q > 0 && q <= sku.cuarentena + 1e-4 && (!pideColor || varianteId !== null);
  const info = ACCION[accion];
  const confirmar = async () => {
    setEnviando(true);
    try {
      await api.post('/api/v3/cuarentena/acciones', { skuInterno: sku.skuInterno, accion, cantidad: q, ...(pideColor ? { varianteId } : {}), ...(notas.trim() ? { notas: notas.trim() } : {}) });
      onListo();
    } catch (e) {
      onError(extractErrorMessage(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={`${info.titulo} · ${textoArticulo(sku.skuInterno, identidad(sku.skuInterno))}`} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">{info.explicacion}</p>
        <p className="text-sm text-slate-700">{`En cuarentena: ${fmtCantidad(sku.cuarentena)}`}</p>
        <div>
          <label htmlFor="cu-cantidad" className="block text-xs font-medium text-slate-600 mb-1">Cantidad</label>
          <input id="cu-cantidad" type="number" min={0} max={sku.cuarentena} step="any" className={claseInput} value={cantidad} onChange={(e) => setCantidad(e.target.value)} />
          {(!Number.isFinite(q) || q <= 0 || q > sku.cuarentena + 1e-4) && <p className="text-xs text-red-600 mt-1">{`Entre 1 y ${fmtCantidad(sku.cuarentena)}`}</p>}
        </div>
        {pideColor && (
          <div>
            <span className="block text-xs font-medium text-slate-600 mb-1">Color al que vuelven las unidades</span>
            <ChipsVariante etiqueta="Color al que vuelven" variantes={(sku.variantes ?? []).map((v) => ({ ...v, cantidad: 0 }))} valor={varianteId}
              onChange={setVarianteId} conSinAsignar={false} />
          </div>
        )}
        <div>
          <label htmlFor="cu-notas" className="block text-xs font-medium text-slate-600 mb-1">Notas</label>
          <textarea id="cu-notas" className={claseInput} rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancelar</button>
          <button onClick={confirmar} disabled={enviando || !valida}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed ${accion === 'liberar' ? 'bg-brand-600 hover:bg-brand-500' : 'bg-red-600 hover:bg-red-500'}`}>
            Confirmar
          </button>
        </div>
      </div>
    </Modal>
  );
}
